import { DynamicEntityFieldSelectorComponent } from './../dynamic-entity-field-selector/dynamic-entity-field-selector.component';
import { DynamicEntityDto, DynamicTemplateDto, DynamicTypeEnum } from '../../../service/dto';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DynamicPropertyDto } from '../../../service/dto';
import { Observable, Subscription } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NzModalService } from 'ng-zorro-antd/modal';
import { HttpDynamicService } from '../../../service/http-dynamic.service';
import { DynamicEntityModalComponent } from '../dynamic-entity-modal/dynamic-entity-modal.component';
import { faArrowDown91, faArrowUp91, faAsterisk, faCode, faKey, faRightLeft, faWarning } from '@fortawesome/free-solid-svg-icons';
import { CanComponentDeactivate } from '../../../components/pending-changes.guard';

export class DynamicPropertyWrapper {
	payload!: DynamicPropertyDto;
	dependents!: DynamicPropertyWrapper[];
	expand!: boolean;
	validSection!: boolean;
}

@Component({
	selector: 'app-dynamic-entity-view-list',
	standalone: false,
	templateUrl: './dynamic-entity-view.component.html',
	styleUrl: './dynamic-entity-view.component.scss'
})
export class DynamicEntityViewComponent implements OnInit, OnDestroy, CanComponentDeactivate {
	newPropertySerial = 0;
	subscription = new Subscription();
	originalDynamicEntityJson?: string;
	dynamicEntity?: DynamicEntityDto;
	wrappers: DynamicPropertyWrapper[] = [];
	templates: DynamicTemplateDto[] = [];
	showAddField = true;

	faReverse = faRightLeft;
	faWarning = faWarning;
	faAsterisk = faAsterisk;
	faKey = faKey;
	faCode = faCode;
	faArrowDown = faArrowDown91;
	faArrowUp = faArrowUp91;




	constructor(
		private httpService: HttpDynamicService,
		private activatedRoute: ActivatedRoute,
		private modalService: NzModalService,
		private router: Router) { }

	ngOnInit(): void {
		this.httpService.getTemplates().subscribe(result => this.templates = result);
		this.subscription.add(
			this.activatedRoute.paramMap.subscribe(params => {
				const id = params.get('id')!;
				this.httpService.get(+id).subscribe(result => {
					this.dynamicEntity = result;
					this.originalDynamicEntityJson = JSON.stringify(result);
					this.templateChanged();
				});
			})
		);
	}
	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}
	canDeactivate(): Observable<boolean> | boolean {
		if (!this.originalDynamicEntityJson) {
			return true;
		}
		if (!this.dynamicEntity) {
			return true;
		}
		if (this.originalDynamicEntityJson == JSON.stringify(this.dynamicEntity)) {
			return true;
		}
		return new Observable<boolean>(observer => {
			this.modalService.confirm({
				nzTitle: 'You have unsaved changes',
				nzContent: 'Do you want to save before leaving?',
				nzOkText: 'Save',
				nzCancelText: 'Discard',
				nzOnOk: () => {
					this.doSave();
					observer.next(true);
					observer.complete();
				},
				nzOnCancel: () => {
					observer.next(true);
					observer.complete();
				}
			});
		});
	}

	templateChanged() {
		this.wrappers = this.parseRoot(this.dynamicEntity!.properties);
		this.showAddField = this.dynamicEntity!.properties.filter(item => item.fieldDisabled == true).length > 0;
	}
	parseRoot(properties: DynamicPropertyDto[]): DynamicPropertyWrapper[] {
		properties = [...properties].sort((a, b) => {
			let out = (a.sectionId ?? 0) - (b.sectionId ?? 0);
			if (out != 0) {
				return out;
			}
			out = (a.orderIndex ?? 0) - (b.orderIndex ?? 0);
			if (out != 0) {
				return out;
			}
			out = (a.name ?? '').localeCompare(b.name ?? '');
			if (out != 0) {
				return out;
			}
			return (a.property ?? '').localeCompare(b.property ?? '');
		});

		const out: DynamicPropertyWrapper[] = [];
		properties
			.filter(item => item.fieldDisabled == false)
			.filter(item => item.dependencyId == undefined)
			.forEach(item => {
				out.push({
					payload: item,
					dependents: this.parse(item, properties),
					expand: false,
					validSection: this.isSectionValid(item)
				});
			});
		return out;
	}
	parse(input: DynamicPropertyDto, properties: DynamicPropertyDto[]): DynamicPropertyWrapper[] {
		const out: DynamicPropertyWrapper[] = [];
		properties
			.filter(item => item.fieldDisabled == false)
			.filter(item => input.id == item.dependencyId)
			.forEach(item => {
				out.push({
					payload: item,
					dependents: this.parse(item, properties),
					expand: false,
					validSection: this.isSectionValid(item)
				});
			});
		return out;
	}
	isSectionValid(input: DynamicPropertyDto): boolean {
		return this.dynamicEntity!.template.sections.filter(item => item.id === input.sectionId).length > 0;
	}
	addDynamicProperty() {
		const modal = this.modalService.create({
			nzTitle: 'Dynamic Property: Add',
			nzContent: DynamicEntityModalComponent,
			nzWidth: '75%',
			nzFooter: null
		});
		modal.componentInstance!.dynamicEntity = this.dynamicEntity!;
		modal.componentInstance!.input = {
			id: --this.newPropertySerial,
			createdAt: new Date(),
			property: '',
			name: '',
			description: '',
			bulkImportColumnName: '',
			type: 'Dynamic',
			dependencyId: undefined,
			sectionId: undefined as any,
			orderIndex: 1000,
			icon: undefined,
			activeOnCreate: true,
			activeOnUpdate: true,
			searchable: false,
			dataType: 'Text',
			isUnique: false,
			minValue: undefined,
			maxValue: undefined,
			required: false,
			regex: undefined,
			dependencyValue: undefined,
			options: [],
			errorMessageUnique: undefined,
			errorMessageRequired: undefined,
			errorMessageRegex: undefined,
			errorMessageMinValue: undefined,
			errorMessageMaxValue: undefined,
			fieldDisabled: false,
			hashCode: 0
		};

		modal.afterClose.subscribe(result => {
			if (result && result.action === 'submit') {
				this.dynamicEntity!.properties.push(result.value);
				this.templateChanged();
			}
		});
	}
	addFieldProperty() {
		const modal = this.modalService.create({
			nzTitle: 'Select Property',
			nzContent: DynamicEntityFieldSelectorComponent,
			nzFooter: null
		});
		modal.componentInstance!.availableFields = this.dynamicEntity?.properties
			// .filter(item => item.type == 'Field')!;
			.filter(item => item.fieldDisabled == true)!;

		modal.afterClose.subscribe(result => {

			if (result) {
				this.viewProperty({
					payload: result,
					dependents: [],
					expand: false,
					validSection: true
				});
			}
		});
	}
	viewProperty(item: DynamicPropertyWrapper) {
		const modal = this.modalService.create({
			nzTitle: 'Dynamic Property Edit: <b>' + item.payload.property + '</b>',
			nzContent: DynamicEntityModalComponent,
			nzWidth: '75%',
			nzFooter: null
		});
		modal.componentInstance!.input = item.payload;
		modal.componentInstance!.dynamicEntity = this.dynamicEntity!;

		modal.afterClose.subscribe(result => {
			if (result && result.action === 'submit') {
				const index = this.dynamicEntity!.properties.findIndex(p => p.id === result.value.id);
				result.value.fieldDisabled = false;
				this.dynamicEntity!.properties[index] = { ...this.dynamicEntity!.properties[index], ...result.value };
			}
			if (result.action === 'delete') {
				if (result.value.type == 'Dynamic') {
					this.dynamicEntity!.properties = this.dynamicEntity!.properties.filter(item => item.id != result.value.id);
				}
				if (result.value.type == 'Field') {
					this.dynamicEntity!.properties.filter(item => item.id == result.value.id)[0].fieldDisabled = true;
				}
			}
			this.templateChanged();
		});
	}

	canSubmit(): boolean {
		return this.wrappers.filter(item => item.validSection == false).length == 0;
	}
	doSave() {
		if (!this.canSubmit()) {
			return;
		}
		this.httpService.edit(this.dynamicEntity!.id, this.dynamicEntity!).subscribe(result => {
			this.originalDynamicEntityJson = JSON.stringify(result);
			this.dynamicEntity = result;
			this.router.navigate(['/dynamic', 'list']);
		});
	}
	compareTemplates = (o1: DynamicTemplateDto, o2: DynamicTemplateDto): boolean => o1 && o2 ? o1.id === o2.id : o1 === o2;
}
